import { HardDriveUpload } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AutosizeTextarea } from '@/components/ui/textarea'
import { FileUploader } from '@/components/ui/uploader'
import type { TServiceQuestion } from '@/interfaces/service-question'
import { TR2Type } from '@/interfaces'
import { TServiceQuestionType } from '@/interfaces/service-question'

type Props = {
  questions: TServiceQuestion[]
}

export default function QuestionAnswerFields({ questions }: Props) {
  const { control } = useFormContext()
  if (!questions.length) return null

  return (
    <div className="space-y-8">
      {questions.map((question, index) => {
        const required = question.required
        const labelNode = (
          <FormLabel>
            {question.label}
            {!required && <span className="ml-1 text-xs text-ghost">※任意</span>}
          </FormLabel>
        )

        if (question.type === TServiceQuestionType.FILE) {
          return (
            <FormField
              key={question.id}
              control={control}
              name={`answers.${index}.files`}
              render={({ field }) => (
                <FormItem>
                  {labelNode}
                  {question.description && (
                    <FormDescription>{question.description}</FormDescription>
                  )}
                  <FormControl>
                    <FileUploader
                      className="max-w-70 rounded-lg"
                      defaultValues={field.value}
                      onChange={field.onChange}
                      type={TR2Type.ProjectSound}
                    >
                      <div className="flex cursor-pointer items-center gap-2 rounded-lg border border-border py-3 px-4 text-sm hover:border-primary/50">
                        <span className="rounded-full bg-ghost-light p-2">
                          <HardDriveUpload className="h-3 w-3" />
                        </span>
                        <span className="text-ghost">ファイルをアップロード</span>
                      </div>
                    </FileUploader>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )
        }

        if (question.type === TServiceQuestionType.SELECT) {
          return (
            <FormField
              key={question.id}
              control={control}
              name={`answers.${index}.value`}
              render={({ field }) => (
                <FormItem>
                  {labelNode}
                  {question.description && (
                    <FormDescription>{question.description}</FormDescription>
                  )}
                  <Select onValueChange={field.onChange} value={field.value || ''}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(question.options || []).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )
        }

        if (question.type === TServiceQuestionType.TEXTAREA) {
          return (
            <FormField
              key={question.id}
              control={control}
              name={`answers.${index}.value`}
              render={({ field }) => (
                <FormItem>
                  {labelNode}
                  {question.description && (
                    <FormDescription>{question.description}</FormDescription>
                  )}
                  <FormControl>
                    <AutosizeTextarea
                      minHeight={100}
                      maxHeight={250}
                      placeholder="回答を入力"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )
        }

        // TEXT
        return (
          <FormField
            key={question.id}
            control={control}
            name={`answers.${index}.value`}
            render={({ field }) => (
              <FormItem>
                {labelNode}
                {question.description && (
                  <FormDescription>{question.description}</FormDescription>
                )}
                <FormControl>
                  <Input
                    placeholder="回答を入力"
                    {...field}
                    value={field.value || ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )
      })}
    </div>
  )
}
